import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bell, Loader2, CheckCircle2, X, GripHorizontal } from 'lucide-react'
import { motion, AnimatePresence, PanInfo } from 'framer-motion'
import { useNotifications } from '../hooks/use-notifications'
import { notificationConfig } from '../config'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import type { Notification } from '../types'

export function ServerNotifications() {
  const [isOpen, setIsOpen] = useState(false)
  const { notifications, isLoading, markAsRead, markAllAsRead } = useNotifications()
  const { toast } = useToast()
  const navigate = useNavigate()
  const safeNotifications = Array.isArray(notifications) ? notifications : []
  const unreadNotifications = safeNotifications.filter((n) => !n.read_at)
  const hasNotifications = unreadNotifications.length > 0
  const notificationRef = useRef<HTMLDivElement>(null)

  // Detectar cliques fora da notificação para fechá-la
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    // Só adiciona o listener se a notificação estiver aberta
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    // Cleanup do event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Se o usuário arrastou para cima por mais de 100px, fecha o painel
    if (info.offset.y < -100) {
      setIsOpen(false)
    }
  }

  const handleMarkAllAsRead = async () => {
    const { success } = await markAllAsRead()
    
    if (success) {
      toast({
        title: 'Notificações lidas',
        description: 'Todas as notificações foram marcadas como lidas.'
      })
    } else {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Não foi possível marcar as notificações como lidas.'
      })
    }
  }

  const handleMarkAsRead = async (notification: Notification) => {
    console.log('Clicou na notificação:', {
      id: notification.id,
      type: notification.type,
      link: notification.link
    })
    
    const { success } = await markAsRead(notification.id)
    
    if (success) {
      // Se houver um link e não for série eternizada, navegar para ele
      if (notification.link && notification.type !== 'series_eternized') {
        console.log('Navegando para:', notification.link)
        setIsOpen(false) // Fechar o painel de notificações
        navigate(notification.link)
      }
    } else {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Não foi possível marcar a notificação como lida.'
      })
    }
  }

  return (
    <div className="relative" ref={notificationRef}>
      {/* Botão de Notificações */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="h-5 w-5" />
        {hasNotifications && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-white">
            {unreadNotifications.length}
          </span>
        )}
      </Button>

      {/* Painel de Notificações */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            drag="y"
            dragDirectionLock
            dragElastic={0.2}
            dragConstraints={{ top: 0, bottom: 0 }}
            onDragEnd={handleDragEnd}
            className={cn(
              "fixed sm:absolute right-4 sm:right-0 top-16 sm:top-12 z-50",
              "w-[calc(100vw-2rem)] sm:w-80 max-w-[420px]",
              "rounded-lg bg-notification-panel",
              "touch-none select-none"
            )}
          >
            {/* Indicador de arraste (apenas em mobile) */}
            <div className="sm:hidden flex justify-center p-1">
              <div className="w-8 h-1 rounded-full bg-muted-foreground/20" />
            </div>

            <div className="p-4">
              {/* Header */}
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-medium">Notificações</h3>
                <div className="flex items-center gap-2">
                  <Link
                    to="/notifications"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setIsOpen(false)}
                  >
                    Ver todas
                  </Link>
                  {hasNotifications && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={handleMarkAllAsRead}
                    >
                      Marcar todas como lidas
                    </Button>
                  )}
                </div>
              </div>

              {/* Lista de Notificações */}
              <div className="space-y-2">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : hasNotifications ? (
                  <AnimatePresence mode="popLayout">
                    {unreadNotifications.map((notification) => {
                      console.log('Tipo de notificação:', notification.type)
                      const config = notificationConfig[notification.type]
                      
                      // Se não houver configuração para o tipo, pular esta notificação
                      if (!config) {
                        console.warn(`Configuração não encontrada para o tipo de notificação: ${notification.type}`)
                        return null
                      }

                      const Icon = config.icon

                      return (
                        <motion.div
                          key={notification.id}
                          layout
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className={cn(
                            "relative flex items-start gap-3 rounded-lg border p-3",
                            config.bgColor,
                            config.borderColor,
                            notification.link && "cursor-pointer hover:opacity-90"
                          )}
                          onClick={() => notification.link && handleMarkAsRead(notification)}
                        >
                          <Icon className={cn("h-5 w-5 mt-0.5", config.textColor)} />
                          
                          <div className="flex-1 space-y-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-medium text-white/95">{notification.title}</p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation() // Evitar que o clique propague para o div pai
                                  handleMarkAsRead(notification)
                                }}
                                className="text-muted-foreground/50 hover:text-muted-foreground"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground">{notification.content}</p>
                          </div>
                        </motion.div>
                      )
                    })}
                  </AnimatePresence>
                ) : (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    Nenhuma notificação
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
} 